import React, { useMemo } from 'react';
import { Modal, Pressable, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Shadow, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';

export interface TonyteModalButton {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'teal';
  onPress: () => void;
  testID?: string;
}

interface TonyteModalProps {
  visible: boolean;
  title: string;
  message?: string;
  icon?: string;
  iconColor?: string;
  buttons: TonyteModalButton[];
  onDismiss?: () => void;
}

const BTN_BG: Record<NonNullable<TonyteModalButton['variant']>, string> = {
  primary: Colors.sunset,
  secondary: 'rgba(255,255,255,0.08)',
  danger: '#ef4444',
  teal: Colors.turquoise,
};

const BTN_TEXT: Record<NonNullable<TonyteModalButton['variant']>, string> = {
  primary: '#fff',
  secondary: 'rgba(255,255,255,0.62)',
  danger: '#fff',
  teal: Colors.navy,
};

const TonyteModal: React.FC<TonyteModalProps> = ({
  visible,
  title,
  message,
  icon,
  iconColor = Colors.turquoise,
  buttons,
  onDismiss,
}) => {
  const { theme } = useTheme();
  const appearance = useMemo(() => createThemeStyles(theme), [theme]);
  const sheen = theme.isDark
    ? ['rgba(255,255,255,0.095)', 'rgba(255,255,255,0.038)']
    : ['rgba(255,255,255,0.78)', 'rgba(255,250,242,0.18)'];
  const wash = theme.isDark
    ? ['rgba(38,208,206,0.07)', 'rgba(242,125,38,0.06)', 'rgba(5,8,18,0.92)']
    : ['rgba(21,155,154,0.06)', 'rgba(242,125,38,0.07)', 'rgba(255,252,246,0.90)'];

  return (
    <Modal
      transparent
      animationType="fade"
      visible={visible}
      statusBarTranslucent
      onRequestClose={onDismiss}
    >
      <Pressable style={[styles.overlay, appearance.overlay]} onPress={onDismiss}>
        <Pressable style={[styles.card, appearance.card]} onPress={() => {}}>
          <LinearGradient colors={sheen} style={StyleSheet.absoluteFill} />
          <LinearGradient
            colors={wash}
            start={{ x: 0.15, y: 0 }}
            end={{ x: 0.9, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
          <LinearGradient
            colors={['#2DD4D7', '#8B5CF6', '#F27D26']}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={styles.reflectiveEdge}
          />
          <View pointerEvents="none" style={styles.cyanGlow} />
          <View pointerEvents="none" style={styles.orangeGlow} />
          <View style={styles.inner}>
            {icon ? (
              <View style={[styles.iconWrap, appearance.iconWrap, { borderColor: `${iconColor}55` }]}>
                <Icon name={icon} size={22} color={iconColor} />
              </View>
            ) : null}
            <Text style={[styles.title, appearance.title]}>{title}</Text>
            {message ? <Text style={[styles.message, appearance.message]}>{message}</Text> : null}
            <View style={[styles.btnRow, buttons.length > 2 && styles.btnRowColumn]}>
              {buttons.map((btn, i) => {
                const variant = btn.variant ?? 'primary';
                const secondaryBg = variant === 'secondary' && !theme.isDark
                  ? theme.colors.controlBackground
                  : BTN_BG[variant];
                const secondaryText = variant === 'secondary' && !theme.isDark
                  ? theme.colors.textSecondary
                  : BTN_TEXT[variant];
                return (
                  <TouchableOpacity
                    key={i}
                    style={[
                      styles.btn,
                      { backgroundColor: secondaryBg },
                      variant === 'secondary' && styles.btnOutline,
                      variant === 'secondary' && appearance.btnOutline,
                      buttons.length <= 2 && styles.btnFlex,
                    ]}
                    onPress={btn.onPress}
                    activeOpacity={0.82}
                    accessibilityRole="button"
                    accessibilityLabel={btn.label}
                    testID={btn.testID}
                  >
                    {variant === 'danger' ? <Icon name="trash-2" size={16} color="#fff" /> : null}
                    <Text style={[styles.btnText, { color: secondaryText }]}>{btn.label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(1,4,13,0.82)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: 'rgba(9,14,29,0.96)',
    borderWidth: 1,
    borderColor: 'rgba(125,211,252,0.24)',
    borderRadius: 28,
    overflow: 'hidden',
    ...Shadow.strong,
  },
  reflectiveEdge: { position: 'absolute', top: 0, left: 28, right: 28, height: 2, borderRadius: 2 },
  cyanGlow: { position: 'absolute', width: 150, height: 150, borderRadius: 75, left: -80, top: -75, backgroundColor: 'rgba(34,211,238,0.10)' },
  orangeGlow: { position: 'absolute', width: 170, height: 170, borderRadius: 85, right: -100, bottom: -110, backgroundColor: 'rgba(242,125,38,0.12)' },
  inner: { padding: 26, gap: 14 },
  iconWrap: {
    width: 58,
    height: 58,
    borderRadius: 19,
    borderWidth: 1.5,
    backgroundColor: 'rgba(255,255,255,0.07)',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    marginBottom: 2,
  },
  title: {
    color: '#fff',
    fontSize: 21,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  message: {
    color: 'rgba(255,255,255,0.62)',
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
  },
  btnRow: { flexDirection: 'row', gap: 10, marginTop: 8 },
  btnRowColumn: { flexDirection: 'column' },
  btn: {
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    flexDirection: 'row',
    gap: 8,
  },
  btnFlex: { flex: 1 },
  btnOutline: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
  },
  btnText: { fontSize: 15, fontWeight: Typography.bold },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  overlay: daylightStyle(theme, { backgroundColor: theme.colors.overlay }),
  card: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.borderStrong,
    ...(theme.shadows?.elevated ?? {}),
  }),
  iconWrap: daylightStyle(theme, { backgroundColor: theme.colors.accentSecondarySoft }),
  title: daylightStyle(theme, { color: theme.colors.textPrimary }),
  message: daylightStyle(theme, { color: theme.colors.textSecondary }),
  btnOutline: daylightStyle(theme, { borderColor: theme.colors.borderStrong }),
});

export default TonyteModal;
